import { edgeHivas, EdgeHiba } from '../lib/edge.ts';
import { supabase } from '../lib/supabase.ts';
import type { HibajegyPrioritas, HibajegyStatusz } from './tipusok.ts';

interface LetrehozasValasz { id: string; feltoltesek: { utvonal: string; token: string }[] }

export interface BekuldesEredmeny { id: string; kepHiba: number }

/**
 * Jegy létrehozása, majd a képek feltöltése és rögzítése. A jegy akkor is megmarad, ha a képek egy része (vagy mind) elbukik:
 * ilyenkor a `kepHiba` a fel nem töltött képek száma.
 */
export async function hibajegyBekuldese(adat: {
  officeId: string; cim: string; leiras: string; prioritas: HibajegyPrioritas; oldalUrl: string | null; kepek: File[];
}): Promise<BekuldesEredmeny> {
  const valasz = await edgeHivas<LetrehozasValasz>('hibajegy', {
    muvelet: 'letrehozas', officeId: adat.officeId, cim: adat.cim.trim(), leiras: adat.leiras.trim(), prioritas: adat.prioritas,
    ...(adat.oldalUrl ? { oldalUrl: adat.oldalUrl } : {}),
    kepek: adat.kepek.map((k) => ({ mime: k.type, meret: k.size })),
  });
  if (adat.kepek.length === 0) return { id: valasz.id, kepHiba: 0 };

  const sikeres: string[] = [];
  await Promise.all(adat.kepek.map(async (kep, i) => {
    const hely = valasz.feltoltesek[i];
    if (!hely) return;
    try {
      const { error } = await supabase.storage.from('hibajegyek').uploadToSignedUrl(hely.utvonal, hely.token, kep);
      if (!error) sikeres.push(hely.utvonal);
    } catch {
      // A feltöltési hibát a hívó a `kepHiba` darabszámmal jelzi a felhasználónak; a jegy ettől még él.
    }
  }));
  if (sikeres.length === 0) return { id: valasz.id, kepHiba: adat.kepek.length };
  try {
    await edgeHivas<{ ok: true }>('hibajegy', { muvelet: 'kepek_rogzites', hibajegyId: valasz.id, utvonalak: sikeres });
  } catch {
    // A rögzítés elbukott: a feltöltött képek nem kerültek a jegyhez, ezért mind hibásnak számít.
    return { id: valasz.id, kepHiba: adat.kepek.length };
  }
  return { id: valasz.id, kepHiba: adat.kepek.length - sikeres.length };
}

export async function hibajegyUzenet(hibajegyId: string, szoveg: string): Promise<void> {
  await edgeHivas<{ ok: true }>('hibajegy', { muvelet: 'uzenet', hibajegyId, szoveg: szoveg.trim() });
}

export async function hibajegyStatusz(hibajegyId: string, statusz: HibajegyStatusz, megjegyzes?: string): Promise<void> {
  const m = megjegyzes?.trim();
  await edgeHivas<{ ok: true }>('hibajegy', { muvelet: 'statusz', hibajegyId, statusz, ...(m ? { megjegyzes: m } : {}) });
}

const HIBA_SZOVEG: Record<string, string> = {
  atmenet: 'A jelentés állapota közben megváltozott. Frissítsd az oldalt.',
  indoklas_kell: 'Az elutasításhoz írd meg az indoklást.',
  nincs_jegy: 'Ez a jelentés nem található.',
  nincs_iroda: 'Az iroda nem található.',
  nincs_fajl: 'A kép feltöltése nem fejeződött be. Próbáld újra.',
  ervenytelen_fajl: 'A kép nem megfelelő (PNG, JPEG vagy WebP, legfeljebb 5 MB).',
  tul_sok_kep: 'Egy jelentéshez legfeljebb 5 kép csatolható.',
};

/** Az `EdgeHiba` hibajegy-specifikus szövege; ismeretlen kódnál `null` (ilyenkor a közös `HibaUzenet` fordít). */
export function hibajegyHibaSzoveg(hiba: unknown): string | null {
  return hiba instanceof EdgeHiba ? HIBA_SZOVEG[hiba.kod] ?? null : null;
}
